/**
 * Adding a bank account.
 *
 * Every bank account mirrors exactly one ledger account, and no ledger account
 * mirrors two. There is deliberately no shared "Bank" head: a payment has to
 * name the account it left from, and once two banks share a ledger account
 * neither can be reconciled — the balance is right and belongs to nobody.
 *
 * So the ledger account is the load-bearing field, and the picker offers only
 * accounts that are postable and not already spoken for. The API refuses the
 * rest; a list that offers them anyway is a list that teaches people to read
 * error messages.
 *
 * A clinic till is a bank account too. Same lifecycle, same reconciliation,
 * same questions asked of it, so it is not a separate kind of thing with a
 * separate screen.
 */

import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, type Account, type Location } from '../api'
import { Card, ErrorNote, Field, Note } from '../components/ui'

interface BankAccountRow {
  id: string
  name: string
  gl_account_id: string
}

/** Four letters for the bank, a zero, six letters or digits for the branch. */
export const IFSC_SHAPE = /^[A-Z]{4}0[A-Z0-9]{6}$/

/** The kinds that sit at a branch. A till has none; a credit card has no IFSC. */
export const KINDS_AT_A_BRANCH = new Set(['current', 'savings', 'overdraft'])

const KINDS: { value: string; label: string; hint: string }[] = [
  { value: 'current', label: 'Current account', hint: '' },
  { value: 'savings', label: 'Savings account', hint: '' },
  { value: 'overdraft', label: 'Overdraft', hint: '' },
  { value: 'credit_card', label: 'Credit card', hint: '' },
  { value: 'cash', label: 'Cash till or petty cash float', hint: 'belongs to a clinic' },
]

export default function AddBankAccount({
  accounts,
  chart,
  locations,
}: {
  accounts: BankAccountRow[]
  chart: Account[]
  locations: Location[]
}) {
  const queryClient = useQueryClient()

  const [name, setName] = useState('')
  const [bankName, setBankName] = useState('')
  const [accountNumber, setAccountNumber] = useState('')
  const [ifsc, setIfsc] = useState('')
  const [kind, setKind] = useState('current')
  const [glAccountId, setGlAccountId] = useState('')
  const [locationId, setLocationId] = useState('')
  const [done, setDone] = useState<string | null>(null)

  /**
   * Postable, and not already mirroring another bank account. Cash-classified
   * accounts first because that is what a bank account almost always is, but
   * the rest stay reachable — an overdraft is a liability, and refusing to
   * show it would be a rule invented here rather than one the books have.
   */
  const available = useMemo(() => {
    const taken = new Set(accounts.map((a) => a.gl_account_id))
    const free = chart.filter((a) => a.is_postable && a.is_active && !taken.has(a.id))
    const isCash = (a: Account) => a.cash_flow_class === 'cash'
    return [...free.filter(isCash), ...free.filter((a) => !isCash(a))]
  }, [accounts, chart])

  const create = useMutation({
    mutationFn: () =>
      api.post<{ name: string }>('/banking/accounts', {
        name: name.trim(),
        bank_name: bankName.trim(),
        account_number: accountNumber.trim(),
        ifsc: ifsc.trim() || null,
        kind,
        gl_account_id: glAccountId,
        location_id: locationId || null,
      }),
    onSuccess: (row) => {
      setDone(row.name)
      setName('')
      setBankName('')
      setAccountNumber('')
      setIfsc('')
      setGlAccountId('')
      setLocationId('')
      void queryClient.invalidateQueries({ queryKey: ['bank-accounts'] })
    },
  })

  const needsClinic = kind === 'cash'
  //: A till has no branch and a credit card has no IFSC, so neither is asked.
  //: Said here as well as by the server so the button explains itself rather
  //: than refusing after the click.
  const needsIfsc = KINDS_AT_A_BRANCH.has(kind)
  const ifscTyped = ifsc.trim()
  const ifscLooksRight = IFSC_SHAPE.test(ifscTyped)
  const ready =
    name.trim() &&
    bankName.trim() &&
    accountNumber.trim() &&
    glAccountId &&
    (!needsClinic || locationId) &&
    (needsIfsc ? ifscLooksRight : !ifscTyped || ifscLooksRight)

  return (
    <Card title="Add a bank account" hint="one ledger account each, never shared">
      <ErrorNote error={create.error} />
      {done && (
        <Note kind="ok">
          {done} is added as a draft. Check it against the bank’s paperwork, then send it for
          approval — nothing can be loaded against it until a Finance Head agrees to it.
        </Note>
      )}

      {available.length === 0 && (
        <Note kind="warn">
          Every postable ledger account is already mirroring a bank account. Add one to
          the chart of accounts first — under Cash and bank, so it lands in the right
          place on the balance sheet.
        </Note>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (ready) create.mutate()
        }}
      >
        <div className="row">
          <Field label="What you call it" caption="shown wherever a payment names an account">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="IDFC Current"
              required
            />
          </Field>
          <Field label="Bank">
            <input
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
              placeholder="IDFC FIRST Bank"
              required
            />
          </Field>
          <Field
            label="Account number"
            caption="the whole number, as printed on the statement"
          >
            <input
              className="mono"
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
              placeholder="50100123456789"
              required
            />
          </Field>
          <Field
            label="IFSC"
            caption={
              needsIfsc
                ? 'the branch code, as printed on the cheque book'
                : 'not needed for a till or a card'
            }
          >
            <input
              className="mono"
              value={ifsc}
              // Capitals as it is typed: sbin0015781 and SBIN0015781 are one
              // branch, and the approval is bound to the exact text.
              onChange={(e) => setIfsc(e.target.value.toUpperCase().replace(/\s/g, ''))}
              placeholder="SBIN0015781"
              maxLength={11}
              required={needsIfsc}
              disabled={!needsIfsc && !ifscTyped}
            />
          </Field>
        </div>

        {ifscTyped && !ifscLooksRight && (
          <Note kind="warn">
            An IFSC is eleven characters: four letters for the bank, a zero, then six
            letters or digits for the branch — <span className="mono">SBIN0015781</span>,
            for example.
            {ifscTyped.length === 11 && ifscTyped[4] !== '0' && ' The fifth character is always a zero.'}
          </Note>
        )}

        <div className="row">
          <Field label="Kind">
            <select value={kind} onChange={(e) => setKind(e.target.value)}>
              {KINDS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                  {k.hint ? ` — ${k.hint}` : ''}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Which account in the books this one is"
            caption="each bank account gets its own; ones already taken are not listed"
          >
            <select
              value={glAccountId}
              onChange={(e) => setGlAccountId(e.target.value)}
              required
            >
              <option value="">Choose a ledger account…</option>
              {available.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code} {a.name}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Clinic"
            caption={needsClinic ? 'required for a till' : 'optional; leave blank for a group account'}
          >
            <select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
              <option value="">{needsClinic ? 'Choose a clinic…' : 'None — the group holds it'}</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {accountNumber.trim().length >= 4 && (
          <Note kind="info">
            Stored in full so a statement can be checked against the account it
            claims to be for — four digits cannot settle that when two accounts at
            one bank share them. Every screen and every audit row shows only{' '}
            <span className="mono">
              {'X'.repeat(Math.max(0, accountNumber.trim().length - 4))}
              {accountNumber.trim().slice(-4)}
            </span>
            .
          </Note>
        )}

        {needsClinic && !locationId && (
          <Note kind="warn">
            A till belongs to a clinic. Without one, the cash it holds is counted against
            the group and appears in no clinic's figures.
          </Note>
        )}

        <button className="primary" type="submit" disabled={!ready || create.isPending}>
          {create.isPending ? 'Adding…' : 'Add this account'}
        </button>
      </form>
    </Card>
  )
}
